import { Menu, Text, Tooltip, useAbility } from '@shared-ui/components'
import { MenuItem } from '@shared-ui/components/Menu/MenuItem'
import { IconGradient } from '@shared-ui/svg'
import React, { type ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'react-responsive'
import { useLocation, useNavigate } from 'react-router-dom'
import { cn, useAppDispatch, useAppSelector } from 'shared/lib'
import { toggleMobileMenu } from 'widgets/Sidebar'
import { menuList } from './consts'

/** Half of the rail's width transition (`duration-300` in MainLayout). */
const RAIL_FADE_MS = 150

/**
 * Crossfades the rail's content in step with its width transition: the
 * current layout fades out over the first half, the layout swaps at the
 * midpoint, and the new one fades in while the rail finishes resizing.
 * Swapping straight away would show the labels wrapping and spilling while
 * the rail is still narrow.
 */
const useRailLayout = (isOpen: boolean) => {
    const [isExpanded, setIsExpanded] = useState(isOpen)
    const [isFading, setIsFading] = useState(false)

    useEffect(() => {
        if (isOpen === isExpanded) {
            setIsFading(false)
            return
        }
        const reduceMotion = window.matchMedia(
            '(prefers-reduced-motion: reduce)'
        ).matches
        if (reduceMotion) {
            setIsExpanded(isOpen)
            return
        }
        setIsFading(true)
        const timeout = setTimeout(() => {
            setIsExpanded(isOpen)
            setIsFading(false)
        }, RAIL_FADE_MS)
        return () => clearTimeout(timeout)
    }, [isOpen, isExpanded])

    return { isExpanded, isFading }
}

export const SidebarMenu = () => {
    const { t } = useTranslation()
    const isMenuOpenState = useAppSelector(
        (state) => state?.globalReducer.isMenuOpen
    )
    // The collapsible rail only exists on large screens. Below that the sidebar
    // is a full drawer and must always render expanded, so it doesn't inherit a
    // collapsed state set on desktop. (matches the lg: width breakpoint)
    const isLargeScreen = useMediaQuery({ minWidth: 1024 })
    const { isExpanded: isMenuOpen, isFading } = useRailLayout(
        isLargeScreen ? isMenuOpenState : true
    )
    const isMobileMenuOpen = useAppSelector(
        (state) => state?.globalReducer.isMobileMenuOpen
    )
    const [activeItem, setActiveItem] = React.useState('')
    const [activeGroup, setActiveGroup] = React.useState('3')

    const { ability } = useAbility()
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const location = useLocation()
    const url = location.pathname.slice(1)

    const handleGroupToggle = (groupId: string) => {
        setActiveGroup((prevGroup) => (prevGroup === groupId ? '' : groupId))
    }
    const menuItems = menuList(ability, navigate, t)

    return (
        <div className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto">
            <div
                className={cn(
                    'transition-opacity duration-150 ease-in-out motion-reduce:transition-none',
                    isFading ? 'opacity-0' : 'opacity-100'
                )}
            >
                {menuItems.map(({ id, groupName, items, isVisible }) => (
                    <div
                        key={id}
                        className={cn(
                            'flex flex-col justify-center border-b-2 border-inactive/20 py-1 align-middle',
                            !isVisible && 'hidden',
                            !isMenuOpen && 'items-center'
                        )}
                    >
                        <div
                            onClick={() => handleGroupToggle(id)}
                            className={cn(
                                'h-full cursor-pointer items-center justify-center py-4 font-sans text-base font-normal leading-tight text-slate-500',
                                isMenuOpen
                                    ? 'px-2 hover:w-full hover:rounded-md hover:bg-inactive/20'
                                    : 'w-full px-1 text-center [text-wrap:balance]'
                            )}
                        >
                            <Text
                                text={groupName}
                                size={isMenuOpen ? 'md' : 'sm'}
                            />
                        </div>
                        {activeGroup === id && (
                            <Menu
                                key={id}
                                menuTitle={''}
                                menuTitleClassName={cn(
                                    '',
                                    !isMenuOpen ? '' : ' '
                                )}
                                className={cn(
                                    !isMenuOpen
                                        ? 'items-center justify-center gap-0 p-0'
                                        : 'gap-0 overflow-y-auto p-0',
                                    'w-full flex-1 overflow-y-auto',
                                    isVisible
                                        ? 'transition ease-in-out'
                                        : 'hidden'
                                )}
                            >
                                {items.map(
                                    ({
                                        label,
                                        icon,
                                        id,
                                        isVisible,
                                        handleItemClick,
                                        renderIcon,
                                    }: {
                                        label: string
                                        icon: string
                                        id: string
                                        isVisible: boolean
                                        handleItemClick: () => void
                                        renderIcon?: (
                                            isActive: boolean
                                        ) => ReactNode
                                    }) => (
                                        <div key={id} className="w-full">
                                            {isVisible && (
                                                <MenuItem
                                                    className={cn(
                                                        'hover:w-full hover:rounded-md hover:bg-inactive/20',
                                                        !isMenuOpen
                                                            ? 'flex w-full items-center justify-center gap-0 align-middle'
                                                            : 'items-center'
                                                    )}
                                                    key={id}
                                                    id={id}
                                                    labelClassName={
                                                        activeItem === id ||
                                                        url ===
                                                            label.toLowerCase()
                                                            ? 'text-background-primary'
                                                            : '!text-lightGrey'
                                                    }
                                                    itemIcon={
                                                        isMenuOpen ? (
                                                            <span
                                                                onClick={() =>
                                                                    setActiveItem(
                                                                        id
                                                                    )
                                                                }
                                                            >
                                                                {renderIcon ? (
                                                                    renderIcon(
                                                                        activeItem ===
                                                                            id ||
                                                                            url ===
                                                                                label.toLowerCase()
                                                                    )
                                                                ) : (
                                                                    <IconGradient
                                                                        fill={
                                                                            activeItem ===
                                                                                id ||
                                                                            url ===
                                                                                label.toLowerCase()
                                                                                ? '#2CAAE2'
                                                                                : '#6B7A99'
                                                                        }
                                                                        d={icon}
                                                                    />
                                                                )}
                                                            </span>
                                                        ) : (
                                                            <Tooltip
                                                                classNames="shadow-light"
                                                                contentSide="right"
                                                                trigger={
                                                                    <span
                                                                        onClick={() => {
                                                                            handleItemClick()
                                                                            setActiveItem(
                                                                                id
                                                                            )
                                                                        }}
                                                                    >
                                                                        {renderIcon ? (
                                                                            renderIcon(
                                                                                activeItem ===
                                                                                    id ||
                                                                                    url ===
                                                                                        label.toLowerCase()
                                                                            )
                                                                        ) : (
                                                                            <IconGradient
                                                                                fill={
                                                                                    activeItem ===
                                                                                        id ||
                                                                                    url ===
                                                                                        label.toLowerCase()
                                                                                        ? '#2CAAE2'
                                                                                        : '#C3CAD9'
                                                                                }
                                                                                d={
                                                                                    icon
                                                                                }
                                                                            />
                                                                        )}
                                                                    </span>
                                                                }
                                                                message={label}
                                                            />
                                                        )
                                                    }
                                                    handleActive={(isActive) =>
                                                        setActiveItem(isActive)
                                                    }
                                                    handleItemClick={() => {
                                                        handleItemClick()
                                                        isMobileMenuOpen &&
                                                            dispatch(
                                                                toggleMobileMenu()
                                                            )
                                                    }}
                                                    label={
                                                        isMenuOpen ? label : ''
                                                    }
                                                />
                                            )}
                                        </div>
                                    )
                                )}
                            </Menu>
                        )}
                    </div>
                ))}
            </div>
        </div>
    )
}
